import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { cn } from "@volca/ui/lib/utils";

type ButtonProps = React.ComponentProps<typeof Button>;

interface TimedButtonProps extends Omit<ButtonProps, "onClick"> {
  timeoutMs?: number;
  onTimeout: () => void;
  onClick?: () => void;
  wrapperClassName?: string;
}

const STEP_MS = 50;

export const TimedButton = React.forwardRef<
  HTMLButtonElement,
  TimedButtonProps
>(
  (
    {
      timeoutMs = 5000,
      onTimeout,
      onClick,
      children,
      disabled,
      className,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const clampedTimeout = Math.max(1, timeoutMs);
    const [timeLeft, setTimeLeft] = React.useState(clampedTimeout);
    const [isActive, setIsActive] = React.useState(true);
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(
      null,
    );

    const progressPercentage = React.useMemo(
      () => ((clampedTimeout - timeLeft) / clampedTimeout) * 100,
      [clampedTimeout, timeLeft],
    );

    const startTimer = React.useCallback(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      setIsActive(true);
      setTimeLeft(clampedTimeout);

      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= STEP_MS) {
            setIsActive(false);
            onTimeout();
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            return 0;
          }
          return prev - STEP_MS;
        });
      }, STEP_MS);
    }, [clampedTimeout, onTimeout]);

    React.useEffect(() => {
      startTimer();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [startTimer]);

    React.useEffect(() => {
      if (timeoutMs !== timeLeft && !isActive) {
        setTimeLeft(timeoutMs);
      }
    }, [timeoutMs, timeLeft, isActive]);

    return (
      <div
        className={cn(
          "inline-flex w-auto items-center justify-center rounded-full bg-brand-bold",
          wrapperClassName,
        )}
      >
        <div
          className="relative overflow-hidden rounded-full"
          style={{
            background: `linear-gradient(to right, var(--primary-progress) ${progressPercentage}%, transparent ${progressPercentage}%)`,
          }}
        >
          <Button
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            className={cn(
              "relative z-10 text-on-brand hover:bg-primary-progress hover:text-on-brand",
              className,
            )}
            {...props}
            variant="ghost"
          >
            {children}
          </Button>
        </div>
      </div>
    );
  },
);

TimedButton.displayName = "TimedButton";
